import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { MyfileChartModel } from '../models/myfile-chart.model';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { InvoiceBalanceSummarySplineAreaChart } from '../models/reporting/invoice-balance-summary-spline-area-chart';
import { DatePipe } from '@angular/common';
import { catchError, map, Observable, of } from 'rxjs';
import { NotificationCountResult } from '../models/push/notification-count-result';
import { NotificationResult } from '../models/push/notification-result';
import { ErrorModel } from '../models/error.model';
import { ToasterNotificationService } from './toaster-notification.service';
import { ClientSearchModel } from '../models/members/client/client-search-model';
import { UserModel } from '../models/members/user/user.model';
import { CreditCardPaymentInfoModel } from '../models/members/user/credit-card-payment-info-model';
import { ContactModel } from '../models/contact/contact.model';
import { ContactSearchModal } from '../models/contact/contacts-search.model';
import { AccessInviteModel } from '../models/access-invite.model';
import { SearchResultModel } from '../models/query/search-result-model';
import { CategoryModel } from '../models/document/category-model';
import { TemplateSearchModel } from '../models/document/template-search-model';
import { DocumentTemplateModel } from '../models/document/document-template-model';
import { CategorySearchModel } from '../models/document/category-search-model';
import { ClientModel } from '../models/members/client/client.model';
import { ClientSignatureModel } from '../models/members/client/client-signature.model';
import { RequisitionModel } from '../models/requisition/requisition.model';
import { MyfileModel } from '../models/myfile/myfile.model';
import { EntityFileUploadModel } from '../models/file/entity-file-upload-model';
import { EntityFileSearchModel } from '../models/file/entity-file-search-model';
import { EntityFileModel } from '../models/file/entity-file-model';
import * as saveAs from 'file-saver';
import { RequisitionBaseModel } from '../models/requisition/requisition-base.model';
import { RequisitionSearchModel } from 'src/app/core/models/requisition/requisition-search.model';
import { TimelineModel } from 'src/app/core/models/timeline/timeline.model';
import { ClientBaseModel } from '../models/members/client/client-base-model';
import { ContactBaseModel } from '../models/contact/contact-base.model';
import { UserBaseModel } from 'src/app/core/models/members/user/user-base.model';
import { ClientReportModel } from '../models/members/client/client-report.model';
import { RequisitionStatusSummaryModel } from '../models/requisition/requisition-status-summary-model.model';
import { RequisitionStatusSummaryListState } from 'src/app/pages/dashboards/dashboard/dashboard.component';
import { TimelineSearchModel } from '../models/timeline/timeline-search.model';
import { BadgeCountResults } from '../models/push/badge-count-results';
import { CheckoutInfoModel } from '../models/cart/checkout-info-model';
import { CheckoutResultInfoModel } from '../models/cart/checkout-result-info-model';
import { ExportConfigModel } from '../models/reporting/export-config-model';
import { MyfileSearchModel } from '../models/myfile/myfile-search.model';
import { ClientSignatureRequestModel } from '../models/members/client/client-signature-request.model';
import { ClientSignatureSubmitInfoModel } from '../models/members/client/client-signature-submit-info.model';
import { UserSearchModel } from '../models/members/user/user-search.model';
import { OperationsSearchModel } from '../models/members/user/operations-search.model';
import { OperationsModel } from '../models/members/user/operations.model';
import { LoginType } from '../models/members/user/user-context.model';
import { AuthContext } from '../models/auth-context.model';
import { AuditLogSearchModel } from '../models/security/audit-log-history.model';
import { AuditLogModel } from '../models/security/audit-log.model';
import { InvoiceModel } from '../models/commerce/invoice.model';
import { InvoiceSearchModel } from '../models/commerce/invoice-search.model';
import { NotificationsSearchModel } from '../models/push/notifications-search.model';
import { NotificationModel } from '../models/push/notification.model';
import { TaskModel } from '../models/task/task.model';
import { ImportEntitySearch } from '../models/import/import-entity-search.model';
import { CalendarEventModel } from '../models/calendar/calendar.model';
import { CalendarEventSearchModel } from '../models/calendar/calendar-search.model';
import { BaseApiService } from './base-api.service';
import { CalendarEventStatusEnum } from '../enums/calendar-status-enum';
import { SettingsModel } from '../models/system/settings.model';
import { BusinessUnitModel } from '../models/system/business-unit.model';
import { AuthMyfileService } from './auth.myfile.service';
import { EmailTemplateSearchModel } from '../models/emailTemplates/email-template-search.model';
import { EmailTemplateBaseModel } from '../models/emailTemplates/email-template.model.ts';
import { InvoiceApprovalRequestModel } from '../models/commerce/invoice-approval-email.model';
import { InvoiceApproveModel } from '../models/commerce/invoice-approve.model';
import { InvoiceRejectModel } from '../models/commerce/invoice-reject.model';
import { WalletSearchModel } from '../models/commerce/wallet-search.model';
import { WalletModel } from '../models/commerce/wallet.model';

const MYFILE_API_URL = `${environment.apiUrl}`;

/**
 * This data service acts as a proxy for working with the MyFile REST API.
 */
@Injectable({
  providedIn: 'root'
})
export class MyfileApiService extends BaseApiService {


  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
    private authService: AuthMyfileService,
    override toasterNotificationService: ToasterNotificationService
  ) {
    super(toasterNotificationService);
  }

  getMyFileDocumentStatusSummaryReport() {
    return this.http.get<MyfileChartModel>(`${MYFILE_API_URL}/myfiles/reports/document-status-summary`)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  getInvoiceBalanceSummaryReport(from: Date, to: Date) {
    var fromDateString = this.datePipe.transform(from, 'yyyy-MM-dd');
    var toDateString = this.datePipe.transform(to, 'yyyy-MM-dd');
    return this.http.get<InvoiceBalanceSummarySplineAreaChart>(`${MYFILE_API_URL}/Invoices/reports/invoice-balance-summary?from=${fromDateString}&to=${toDateString}`)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  sendEmailMessage(input: any) {
    const headers = { 'content-type': 'application/json' }
    return this.http.post(MYFILE_API_URL + "v1/email/send/template", input, { 'headers': headers })
      .pipe(
        map((response) => {
          if (response) {
            return response;
          } else {
            return null;
          }
        },
        ),
        catchError(err => this.handleError(err))
      )
  }

  createAccessInvite(input: AccessInviteModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/AccessInvites/new`;
    return this.http.post<boolean>(url, JSON.stringify(input), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  /**
   * The proper way to return a list of entities using th SearchResultModel generic class. 
   * A list should contain only the bear minimum of 
   * data usually held in a base model. The full entity is never returned in a list unless
   * that's deemed to be desired. 
   * Returns a list of clients filtered and paged according to @see ClientSearchModel.
   * @param search The search criteria
   * @returns A search result of @see ClientBaseModel
   */
  getClients(search: ClientSearchModel): Observable<SearchResultModel<ClientBaseModel> | undefined> {
    const url = `${MYFILE_API_URL}/clients/`;
    return this.http.post<SearchResultModel<ClientBaseModel>>(url, JSON.stringify(search)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  getClient(clientId?: string, loginType?: LoginType) {
    var url;
    if (loginType && loginType == LoginType.Client) {
      url = `${MYFILE_API_URL}/Clients`;
    } else {
      if (clientId) {
        url = `${MYFILE_API_URL}/Clients/${clientId}`;
      } else {
        url = `${MYFILE_API_URL}/Clients`;
      }
    }

    return this.http.get<ClientModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  getClientSignature(client: ClientModel) {
    const url = `${MYFILE_API_URL}/Clients/${client.id}/signature`;
    return this.http.get<ClientSignatureModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  precreateClient(): Observable<ClientModel | undefined> {
    const url = `${MYFILE_API_URL}/Clients/new`;
    return this.http.post<ClientModel>(url, null)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  clientsReport() {
    const url = `${MYFILE_API_URL}/Clients/reports/client-status-summary`;
    return this.http.get<ClientReportModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  createClient(client: ClientModel): Observable<ClientModel | undefined> {
    const url = `${MYFILE_API_URL}/Clients/new`;
    return this.http.post<ClientModel>(url, JSON.stringify(client))
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  updateClient(client: ClientModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/Clients/${client.id}`;
    return this.http.put<boolean>(url, JSON.stringify(client), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  deleteClient(clientId: string): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/Clients/${clientId}`;

    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  getAuditLogs(search: AuditLogSearchModel): Observable<SearchResultModel<AuditLogModel>> {
    const url = `${MYFILE_API_URL}/Security/login-history`;
    return this.http.post<SearchResultModel<AuditLogModel>>(url, JSON.stringify(search));
  }

  getNotifications(search: NotificationsSearchModel): Observable<SearchResultModel<NotificationModel> | undefined> {
    const url = `${MYFILE_API_URL}/Notifications`;
    return this.http.post<SearchResultModel<NotificationModel> | undefined>(url, JSON.stringify(search)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  getImportEntities(search: ImportEntitySearch): Observable<SearchResultModel<TaskModel> | undefined> {
    const url = `${MYFILE_API_URL}/ImportEntitites`;
    return this.http.post<SearchResultModel<TaskModel> | undefined>(url, JSON.stringify(search)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  newTaskContactExport(): Observable<TaskModel | undefined> {
    const url = `${MYFILE_API_URL}/Accounts/tasks/metadata/export`;
    return this.http.get<TaskModel | undefined>(url).pipe(
      map(data => {
        if (data) {
          return data;
        }
        this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
        return undefined;
      }),
      catchError(err => this.handleError(err))
    );
  }

  newTaskContactImport(): Observable<TaskModel | undefined> {
    const url = `${MYFILE_API_URL}/Accounts/tasks/metadata/import`;
    return this.http.get<TaskModel | undefined>(url).pipe(
      map(data => {
        if (data) {
          return data;
        }
        this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
        return undefined;
      }),
      catchError(err => this.handleError(err))
    );
  }

  newTaskClientExport(): Observable<TaskModel | undefined> {
    const url = `${MYFILE_API_URL}/Clients/tasks/metadata/export`;
    return this.http.get<TaskModel | undefined>(url).pipe(
      map(data => {
        if (data) {
          return data;
        }
        this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
        return undefined;
      }),
      catchError(err => this.handleError(err))
    );
  }


  newTaskClientImport(): Observable<TaskModel | undefined> {
    const url = `${MYFILE_API_URL}/Clients/tasks/metadata/import`;
    return this.http.get<TaskModel | undefined>(url).pipe(
      map(data => {
        if (data) {
          return data;
        }
        this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
        return undefined;
      }),
      catchError(err => this.handleError(err))
    );
  }

  /**
   * This set of functions where here before i started coding, i did not have the time to go through them each properly and they seem to be calling methods that do not exist. 
   * 
   */

  getNotificationCount(): Observable<NotificationCountResult> {
    const url = `${MYFILE_API_URL}/notifications/count`;
    return this.http.get<NotificationCountResult>(url);
  }

  //only call that end point out of the 4 that exists 
  getBadgeCounts(): Observable<BadgeCountResults | undefined> {
    const url = `${MYFILE_API_URL}/users/badge-counts`;
    return this.http.get<BadgeCountResults>(url).pipe(
      catchError(err => this.handleError(err))
    );
  }

  getNotificationMessages(): Observable<Array<NotificationResult>> {
    const url = `${MYFILE_API_URL}/notifications`;
    return this.http.get<Array<NotificationResult>>(url);
  }

  deleteNotifications(notificationId: string): Observable<any> {
    const url = `${MYFILE_API_URL}/Notifications/${notificationId}`;

    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  deleteNotificationsList(search: NotificationsSearchModel): Observable<any> {
    const url = `${MYFILE_API_URL}/Notifications`;

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // return this.http.request('DELETE', `${url}`, {
    //   body: JSON.stringify(search),
    //   headers: headers,
    //   responseType: 'json'
    // });


    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response', body: JSON.stringify(search) })
      .pipe(
        map(data => {
          if (data) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  updateNotificationToRead(notificationId: string): Observable<any> {
    const url = `${MYFILE_API_URL}/notifications?id=${notificationId}`;
    return this.http.put<boolean>(url, {}, { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        })
      )
  }



  getUser(userId?: string, tenantId?: String): Observable<UserModel | undefined> {
    var url = `${MYFILE_API_URL}/users`;
    if (userId) {
      url = `${url}/${userId}`
    }
    if (tenantId) {
      url = `${url}/tenant/${tenantId}`
    }
    return this.http.get<UserModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  getUsers(search: UserSearchModel): Observable<SearchResultModel<UserBaseModel> | undefined> {
    const url = `${MYFILE_API_URL}/users/`;
    return this.http.post<SearchResultModel<UserBaseModel>>(url, JSON.stringify(search)).pipe(
      map(data => {
        if (data) {
          return data;
        }
        return undefined;
      }),
      catchError(err => this.handleError(err))
    );;
  }

  updateUser(user: UserModel, tenantId?: string): Observable<boolean | undefined> {
    var url = `${MYFILE_API_URL}/users/${user.id}`;
    if (tenantId) {
      url = `${url}/tenant/${tenantId}`
    }
    return this.http.put<boolean>(url, JSON.stringify(user))
      .pipe(
        map(data => {
          if (data) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }


  exportUserList(exportConfig: ExportConfigModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/users/reports/export`;
    return this.postToExportEndpoint(url, exportConfig);
  }

  uploadStagingFile(formData: FormData): Observable<string | undefined> {
    const url = `${MYFILE_API_URL}/FileService/UploadStagingFile`
    return this.http.post(url, formData, { observe: 'body', responseType: 'text' })
      .pipe(
        map(data => {
          if (data) {
            var fileId: any = data;

            return fileId
          }
          this.toasterNotificationService.showError($localize`:@@:Profile picture failed to load.`, $localize`:@@:45443`);
          return of(undefined);
        }),
        catchError(err => {
          if (err.error instanceof ErrorModel) {

            this.toasterNotificationService.showError(err.error.message, $localize`:@@:12321`);
          }

          return of(undefined);
        })
      )
  }

  enableUsers(userIds: string[]): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/users/enable-users`;
    return this.http.post<boolean | undefined>(url, JSON.stringify(userIds), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200 || data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  disableUsers(userIds: string[]): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/users/disable-users`;
    return this.http.post<boolean>(url, JSON.stringify(userIds), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200 || data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }
  createUser(user: UserModel, tenantId?: string): Observable<string | undefined> {
    var url = `${MYFILE_API_URL}/users`;
    if (tenantId) {
      url = `${url}/tenant/${tenantId}`
    }
    url = `${url}/new`
    return this.http.post<string>(url, JSON.stringify(user), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            var userId: any = data.body?.valueOf()
            userId = userId.id;
            return userId
          }
          return of(undefined);
        }),
        catchError(err => this.handleError(err))
      )
  }

  getSecurityOperations(search: OperationsSearchModel): Observable<OperationsModel> {
    const url = `${MYFILE_API_URL}/Security/Operations`;
    return this.http.post<OperationsModel>(url, JSON.stringify(search));
  }

  getSecurityAuthContext(ownerId: string): Observable<any | undefined> {
    const url = `${MYFILE_API_URL}/Security/AuthorizationContext`;
    var search = { 'ownerId': `${ownerId}` }
    return this.http.post<AuthContext>(url, JSON.stringify(search), { observe: 'response' })
      .pipe(
        map(data => {
          return data.body?.valueOf()
        }),
        catchError(err => this.handleError(err))
      )
  }

  getSecurityAuthContextWithTenant(ownerId: string, tenantId: string): Observable<any | undefined> {
    const url = `${MYFILE_API_URL}/Security/AuthorizationContext/tenant/${tenantId}`;
    var search = { 'ownerId': `${ownerId}` }
    return this.http.post<AuthContext>(url, JSON.stringify(search), { observe: 'response' })
      .pipe(
        map(data => {
          return data.body?.valueOf()
        }),
        catchError(err => this.handleError(err))
      )
  }

  updateSecurityAuthContext(ownerContext: AuthContext): Observable<boolean> {
    const url = `${MYFILE_API_URL}/Security/AuthorizationContext`;
    return this.http.put<boolean>(url, JSON.stringify(ownerContext), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        })
      )
  }

  updateSecurityAuthContextWithTenant(ownerContext: AuthContext, tenantId: string): Observable<boolean> {
    const url = `${MYFILE_API_URL}/Security/AuthorizationContext/${tenantId}`;
    return this.http.put<boolean>(url, JSON.stringify(ownerContext), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        })
      )
  }

  getInvoice(myFileInvoiceId: string): Observable<InvoiceModel | undefined> {
    const url = `${MYFILE_API_URL}/Invoices`;
    const json = `{"myFileIds": ["${myFileInvoiceId}"]}`;
    return this.http.post<any>(url, json, { observe: 'response' })
      .pipe(
        map(data => {
          var invoice: InvoiceModel | undefined = undefined;
          if (data.body && data.body.items[0]) {
            invoice = data.body.items[0];
          }
          return invoice;
        }),
        catchError(err => this.handleError(err))
      )
  }

  updateInvoice(invoice: InvoiceModel): Observable<any> {
    const url = `${MYFILE_API_URL}/Invoices/${invoice.id}`;
    return this.http.put<boolean>(url, JSON.stringify(invoice), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  getInvoices(search: InvoiceSearchModel): Observable<any> {
    const url = `${MYFILE_API_URL}/Invoices`;
    return this.http.post<SearchResultModel<MyfileModel>>(url, JSON.stringify(search), { observe: 'response' })
      .pipe(
        map(data => {
          return data.body
        }),
        catchError(err => this.handleError(err))
      )
  }

  sendEmailInvoiceApprovalRequest(emailModel: InvoiceApprovalRequestModel): Observable<boolean | undefined> {
    return this.http.post(MYFILE_API_URL + "/invoices/routing/send-approval-request", emailModel)
      .pipe(
        map((result) => {
          return true
        }),
        catchError(err => this.handleError(err))
      );
  }

  responseInvoiceApproval(invoiceApprovalSubmitModel: InvoiceApproveModel): Observable<boolean | undefined> {
    return this.http.post(MYFILE_API_URL + "/invoices/routing/approve", invoiceApprovalSubmitModel)
      .pipe(
        map((result) => {
          return true
        }),
        catchError(err => this.handleError(err))
      );
  }

  responseInvoiceRejection(invoiceApprovalSubmitModel: InvoiceRejectModel): Observable<boolean | undefined> {
    return this.http.post(MYFILE_API_URL + "/invoices/routing/reject", invoiceApprovalSubmitModel)
      .pipe(
        map((result) => {
          return true
        }),
        catchError(err => this.handleError(err))
      );
  }

  createRequisitionChequeMyfile(requisitionId: string) {
    const url = `${MYFILE_API_URL}/requisitions/${requisitionId}/create-cheque-myfile`;
    return this.http.get<any>(url).pipe(
      map(data => {
        return data;
      }),
      catchError(err => this.handleError(err)));
  }

  createRequisitionInvoiceMyfile(requisitionId: string, includesTax: boolean) {
    const url = `${MYFILE_API_URL}/requisitions/${requisitionId}/create-invoice-myfile/${includesTax}`;
    return this.http.get<any>(url).pipe(
      map(data => {
        return data;
      }),
      catchError(err => this.handleError(err)));
  }

  isCreditCardValid(creditcard: CreditCardPaymentInfoModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/BusinessServices/IsCreditCardValid`;
    return this.http.post<boolean>(url, JSON.stringify(creditcard), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  getSystemPublicSettings(): Observable<any> {
    var url = `${MYFILE_API_URL}/System/public-settings`
    return this.http.get<any>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  getSystemGlobalSettings(): Observable<any> {
    var url = `${MYFILE_API_URL}/System/global-settings`
    return this.http.get<any>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  updateSystemSettings(settings: SettingsModel): Observable<boolean> {
    const url = `${MYFILE_API_URL}/System`;
    return this.http.put<boolean>(url, JSON.stringify(settings), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        })
      )
  }


  getSystemTenants(): Observable<any> {
    var url = `${MYFILE_API_URL}/System/tenants`
    return this.http.get<any>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }
  getBusinessUnitForCurrentUser(): Observable<BusinessUnitModel | undefined> {
    var url = `${MYFILE_API_URL}/BusinessUnits/GetBusinessUnitForCurrentUserContext`
    return this.http.get<BusinessUnitModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  updateBusinessUnit(businessUnitModel: BusinessUnitModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/BusinessUnits/${businessUnitModel.id}`;
    return this.http.put<boolean>(url, JSON.stringify(businessUnitModel), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  getWallet(search: WalletSearchModel): Observable<SearchResultModel<WalletModel> | undefined> {
    const url = `${MYFILE_API_URL}/wallets`;
    return this.http.post<SearchResultModel<WalletModel>>(url, JSON.stringify(search)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  updateWallet(wallet: WalletModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/wallets`;
    return this.http.put<boolean>(url, JSON.stringify(wallet), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        })
      )
  }


  isSinValid(sinNumber: string): Observable<boolean | undefined> {
    var url = `${MYFILE_API_URL}/BusinessServices/IsSinValid?sin=${sinNumber}`;
    return this.http.get<boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  getEmailTemplates(search: EmailTemplateSearchModel): Observable<SearchResultModel<EmailTemplateBaseModel> | undefined> {
    const url = `${MYFILE_API_URL}/email`;
    return this.http.post<SearchResultModel<EmailTemplateBaseModel>>(url, JSON.stringify(search)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  updateEmailTemplate(email: EmailTemplateBaseModel): Observable<boolean> {
    const url = `${MYFILE_API_URL}/email/${email.id}`;
    return this.http.put<boolean>(url, JSON.stringify(email), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        })
      )
  }

  deleteEmailTemplate(emailId: string): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/email/${emailId}`;

    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  createEmailTemplate(email: EmailTemplateBaseModel): Observable<EmailTemplateBaseModel | undefined> {
    const url = `${MYFILE_API_URL}/email/new`;
    return this.http.post<EmailTemplateBaseModel>(url, JSON.stringify(email)).pipe(
      map((result: EmailTemplateBaseModel) => {
        return result;
      }),
      catchError(err => this.handleError(err))
    );
  }

  getContacts(search: ContactSearchModal): Observable<SearchResultModel<ContactBaseModel> | undefined> {
    const url = `${MYFILE_API_URL}/Accounts`;
    return this.http.post<SearchResultModel<ContactBaseModel>>(url, JSON.stringify(search)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  getContact(contactId: string) {
    const url = `${MYFILE_API_URL}/Accounts/${contactId}`;
    return this.http.get<ContactModel>(url);
  }

  updateContact(contact: ContactModel): Observable<boolean> {
    const url = `${MYFILE_API_URL}/Accounts/${contact.id}`;
    return this.http.put<boolean>(url, JSON.stringify(contact), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        })
      )
  }

  deleteContact(contactId: string): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/Accounts/${contactId}`;

    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  createContact(contact: ContactModel): Observable<ContactModel | undefined> {
    const url = `${MYFILE_API_URL}/Accounts/new`;
    return this.http.post<ContactModel>(url, JSON.stringify(contact)).pipe(
      map((result: ContactModel) => {
        return result;
      }),
      catchError(err => this.handleError(err))
    );
  }

  createRequisition(documentTemplate: string) {
    const url = `${MYFILE_API_URL}/Requisitions/create/${documentTemplate}`;
    return this.http.post<RequisitionModel>(url, '');
  }

  newRequisition(requisition: RequisitionBaseModel): Observable<RequisitionModel | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/new`;
    return this.http.post<RequisitionModel>(url, JSON.stringify(requisition), { observe: 'response' })
      .pipe(
        map((result: any) => {
          return result.body;
        }),
        catchError(err => this.handleError(err))
      );
  }

  getRequisition(requisitionId: string): Observable<RequisitionModel | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/${requisitionId}`;
    return this.http.get<RequisitionModel>(url).pipe(
      map(data => {
        if (data == null) {
          return undefined;
        }
        return data;
      }),
      catchError(err => this.handleError(err))
    );
  }

  getRequisitionList(search: RequisitionSearchModel): Observable<SearchResultModel<RequisitionModel> | undefined> {
    const url = `${MYFILE_API_URL}/requisitions/`;
    return this.http.post<SearchResultModel<RequisitionModel>>(url, JSON.stringify(search)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  getRequisitionStatusSummary(search: RequisitionStatusSummaryListState): Observable<RequisitionStatusSummaryModel> {
    const url = `${MYFILE_API_URL}/Requisitions/reports/requisition-status-summary`;
    return this.http.post<RequisitionStatusSummaryModel>(url, JSON.stringify(search));
  }

  deleteRequisition(requisitionId: string): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/${requisitionId}`;
    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@:${data.body}`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  updateRequisition(requisition: RequisitionModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/${requisition.id}`;
    return this.http.put<boolean>(url, JSON.stringify(requisition), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  updateRequisitionStatus(requisition: RequisitionModel, reason: string): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/updatestatus`;
    const json = `{"requisitionId": "${requisition.id}", "updateToStatus": ${requisition.status},"reason": "${reason}"}`;
    return this.http.post<boolean>(url, json, { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }


  newMyFile(myfile: MyfileModel): Observable<MyfileModel | undefined> {
    const url = `${MYFILE_API_URL}/MyFiles/new`;
    return this.http.post<MyfileModel>(url, JSON.stringify(myfile))
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  createMyFileTemplate(templateId: string) {
    var url = `${MYFILE_API_URL}/MyFiles/create?templateId=${templateId}`;
    return this.http.get<MyfileModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  createMyFile(templateId: string, sourceId: string, persist?: boolean) {
    var url = `${MYFILE_API_URL}/MyFiles/create?templateId=${templateId}&sourceId=${sourceId}`;
    if (persist != undefined) {
      url = url + `&persist=${persist}`
    }
    return this.http.get<MyfileModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  getMyFile(myFileId: string) {
    const url = `${MYFILE_API_URL}/MyFiles/${myFileId}`;
    return this.http.get<MyfileModel>(url)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  deleteMyFile(myFileId: string): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/MyFiles/${myFileId}`;
    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  getMyFileList(search: MyfileSearchModel): Observable<SearchResultModel<MyfileModel> | undefined> {
    const url = `${MYFILE_API_URL}/MyFiles`;
    return this.http.post<SearchResultModel<MyfileModel>>(url, JSON.stringify(search))
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  viewMyFile(requisitionId: string, myFileId: string): Observable<Blob | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/${requisitionId}/myfile/${myFileId}`;
    return this.http.post<Blob>(url, '', { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: any) => {
          if (result) {
            var contentDisposition = result.headers.get("Content-Disposition");
            if (contentDisposition) {
              const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = fileNameRegex.exec(contentDisposition);
              if (matches != null && matches[1]) {
                var contentType = result.headers.get("Content-Type");
                return new Blob([result.body as Blob], { type: contentType as string });
              }
            }
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later. `, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return undefined;
        }),
        catchError(err => this.handleError(err))
      );
  }
  viewMyFileUrl(token: string, requisitionId: string, myFileId: string): Observable<any | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/${requisitionId}/myfile/${myFileId}`;
    const headers = { 'content-type': 'application/json', "Authorization": "Bearer " + token }
    return this.http.post<Blob>(url, '', { 'headers': headers, responseType: 'blob' as 'json', observe: 'response' },)
      .pipe(
        map((result: any) => {
          return result;
        }),
        catchError(err => this.handleError(err))
      );
  }



  authTokenCheck(token: string) {
    const url = `${MYFILE_API_URL}/auth/claims`;
    const headers = { 'content-type': 'application/json' }
    return this.http.post(url, `"${token}"`, { 'headers': headers })
      .pipe(
        map((response) => {
          if (response) {
            return response;
          } else {
            return null;
          }
        },
        ),
        catchError(err => this.handleError(err))
      )
  }

  printMyFile(requisitionId: string, myFileId: string): Observable<Blob | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/${requisitionId}/myfile/${myFileId}`;
    return this.http.post<Blob>(url, '', { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: any) => {
          if (result) {
            var contentDisposition = result.headers.get("Content-Disposition");
            if (contentDisposition) {
              const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = fileNameRegex.exec(contentDisposition);
              if (matches != null && matches[1]) {
                var contentType = result.headers.get("Content-Type");
                var blob = new Blob([result.body as Blob], { type: contentType as string });
                var fileURL = URL.createObjectURL(blob);
                var iframe = document.createElement('iframe'); //load content in an iframe to print later
                document.body.appendChild(iframe);
                iframe.style.display = 'none';
                iframe.src = fileURL;
                iframe.onload = function () {
                  setTimeout(function () {
                    iframe.focus();
                    if (iframe.contentWindow) {
                      iframe.contentWindow.print();
                    }
                  }, 1);
                };
              }
            }
          }
          return result;
        }),
        catchError(err => this.handleError(err))
      );
  }

  downloadMyFile(requisitionId: string, myFileId: string): Observable<Blob | undefined> {
    const url = `${MYFILE_API_URL}/Requisitions/${requisitionId}/myfile/${myFileId}`;
    return this.http.post<Blob>(url, '', { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: any) => {
          if (result) {
            var contentDisposition = result.headers.get("Content-Disposition");
            if (contentDisposition) {
              const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = fileNameRegex.exec(contentDisposition);
              if (matches != null && matches[1]) {
                var contentType = result.headers.get("Content-Type");
                var blob = new Blob([result.body as Blob], { type: contentType as string });
                var fileName = matches[1].replace(/['"]/g, '');
                saveAs(blob, fileName);
              }
            }
          }
          return result;
        }),
        catchError(err => this.handleError(err))
      );
  }

  updateMyFile(myFile: MyfileModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/MyFiles/${myFile.id}`;
    return this.http.put<boolean>(url, JSON.stringify(myFile), { observe: 'response' })
      .pipe(
        map(data => {
          return true;
        }),
        catchError(err => this.handleError(err))
      )
  }

  getDocumentCategories(search: CategorySearchModel): Observable<SearchResultModel<CategoryModel> | undefined> {
    const url = `${MYFILE_API_URL}/Templates/categories`;
    return this.http.post<SearchResultModel<CategoryModel>>(url, JSON.stringify(search))
      .pipe(
        catchError(err => this.handleError(err))
      )
  }

  getDocumentTemplates(search: TemplateSearchModel): Observable<SearchResultModel<DocumentTemplateModel> | undefined> {
    const url = `${MYFILE_API_URL}/Templates`;
    return this.http.post<SearchResultModel<DocumentTemplateModel>>(url, JSON.stringify(search))
      .pipe(
        catchError(err => this.handleError(err))
      )
  }

  createDocumentTemplate(model: DocumentTemplateModel): Observable<DocumentTemplateModel | undefined> {
    const url = `${MYFILE_API_URL}/Templates/new`;

    return this.http.post<DocumentTemplateModel>(url, JSON.stringify(model)).pipe(
      map((result: DocumentTemplateModel) => {
        return result;
      }),
      catchError(err => this.handleError(err))
    );
  }

  updateDocumentTemplate(model: DocumentTemplateModel): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/Templates/${model.id}`;

    return this.http.put<ErrorModel | boolean>(url, JSON.stringify(model), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  deleteDocumentTemplate(modelId: string): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/Templates/${modelId}`;

    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.returnTrue(err))
        // catchError(err => this.handleError(err))
      );
  }


  //remove EntityFileUploadModel once it out of the profile parts of users/clients 
  processEntityFiles(fileUpload: EntityFileModel | EntityFileUploadModel): Observable<any> {
    const url = `${MYFILE_API_URL}/FileService/ProcessEntityFiles`;
    return this.http.post<boolean>(url, JSON.stringify(fileUpload), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return data.body?.valueOf()
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      );
  }

  getPageableFileList(search: EntityFileSearchModel): Observable<SearchResultModel<EntityFileModel> | undefined> {
    const url = `${MYFILE_API_URL}/FileService/GetPageableFileList`;
    return this.http.post<SearchResultModel<EntityFileModel>>(url, JSON.stringify(search))
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  downloadEntityFile(entityTypeId: number, entityId: string, fileId: string): Observable<HttpResponse<Blob> | undefined> {
    const url = `${MYFILE_API_URL}/FileService/Download?entityTypeId=${entityTypeId}&entityId=${entityId}&fileId=${fileId}`;
    return this.http.get<Blob>(url, { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: HttpResponse<Blob>) => {

          if (result) {
            var contentDisposition = result.headers.get("Content-Disposition");

            if (contentDisposition) {

              const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = fileNameRegex.exec(contentDisposition);

              if (matches != null && matches[1]) {
                var fileName = matches[1].replace(/['"]/g, '');
                var contentType = result.headers.get("Content-Type");
                var blob = new Blob([result.body as Blob], { type: contentType as string });
                saveAs(blob, fileName);
              }
            }
          }
          return result;
        }),
        catchError((err: any) => this.handleError(err, false))
      );
  }

  batchDownloadEntityFiles(fileUpload: EntityFileUploadModel): Observable<any> {
    const url = `${MYFILE_API_URL}/FileService/Download-all-files`;
    return this.http.post<Blob>(url, JSON.stringify(fileUpload), { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: HttpResponse<Blob>) => {

          if (result) {
            var contentDisposition = result.headers.get("Content-Disposition");

            if (contentDisposition) {

              const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = fileNameRegex.exec(contentDisposition);

              if (matches != null && matches[1]) {
                var fileName = matches[1].replace(/['"]/g, '');
                var contentType = result.headers.get("Content-Type");
                var blob = new Blob([result.body as Blob], { type: contentType as string });
                saveAs(blob, fileName);
              }
            }
          }
          return result;
        }),
        catchError(err => this.handleError(err, false))
      );
  }

  viewEntityFile(entityTypeId: number, entityId: string, fileId: string): Observable<Blob | undefined> {
    const url = `${MYFILE_API_URL}/FileService/Download?entityTypeId=${entityTypeId}&entityId=${entityId}&fileId=${fileId}`;
    return this.http.get<Blob>(url, { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: any) => {
          if (result) {
            var contentDisposition = result.headers.get("Content-Disposition");
            if (contentDisposition) {
              const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = fileNameRegex.exec(contentDisposition);
              if (matches != null && matches[1]) {
                var contentType = result.headers.get("Content-Type");

                //if result is a pdf, lets our in pdf viewier, handle it else opens the result (image) in the new window as before. 
                if (contentType == "application/pdf") {
                  return new Blob([result.body as Blob], { type: contentType as string });
                }

                var blob = new Blob([result.body as Blob], { type: contentType as string });
                var fileURL = URL.createObjectURL(blob);
                window.open(fileURL);
                return undefined;
              }
            }
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later. `, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return undefined;
        }),
        catchError((err: any) => this.handleError(err))
      );
  }

  getEntityFile(entityTypeId: number, entityId: string, fileId: string, fullResolution?: boolean): Observable<Blob | undefined> {
    var url = '';
    if (fullResolution == true) {
      url = `${MYFILE_API_URL}/FileService/Download?entityTypeId=${entityTypeId}&entityId=${entityId}&fileId=${fileId}&thumbnail=false`;
    } else {
      url = `${MYFILE_API_URL}/FileService/Download?entityTypeId=${entityTypeId}&entityId=${entityId}&fileId=${fileId}`;
    }

    return this.http.get<Blob>(url, { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: HttpResponse<Blob>) => {
          var contentType = result.headers.get("Content-Type");
          return new Blob([result.body as Blob], { type: contentType as string });

        }),
        catchError(err => this.handleError(err, false))
      );
  }

  getTimelinesList(search: TimelineSearchModel): Observable<SearchResultModel<TimelineModel> | undefined> {
    const url = `${MYFILE_API_URL}/TimeLines`;
    return this.http.post<SearchResultModel<TimelineModel>>(url, JSON.stringify(search))
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  newTimelineItem(timeline: TimelineModel): Observable<TimelineModel | undefined> {
    const url = `${MYFILE_API_URL}/TimeLines/new`;
    return this.http.post<TimelineModel>(url, JSON.stringify(timeline)).pipe(
      catchError(err => this.handleError(err))
    );
  }

  updateTimelineItem(timeline: TimelineModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/TimeLines/${timeline.id}`;
    return this.http.put<boolean>(url, JSON.stringify(timeline), { observe: 'response' })
      .pipe(
        map(data => {
          return true;
        }),
        catchError(err => this.handleError(err))
      );
  }

  cartCheckout(checkoutInfo: CheckoutInfoModel): Observable<CheckoutResultInfoModel | undefined> {
    const url = `${MYFILE_API_URL}/Cart/cart-checkout`;
    return this.http.post<CheckoutResultInfoModel>(url, JSON.stringify(checkoutInfo))
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  getCalendars(search: CalendarEventSearchModel): Observable<SearchResultModel<CalendarEventModel>> {
    const url = `${MYFILE_API_URL}/CalendarEvents`;
    return this.http.post<SearchResultModel<CalendarEventModel>>(url, JSON.stringify(search));
  }

  getCalendar(calendarId: string) {
    const url = `${MYFILE_API_URL}/CalendarEvents/${calendarId}`;
    return this.http.get<CalendarEventModel>(url);
  }

  updateCalendarUserStatus(calendarId: string, userEmail: string, newStatus: CalendarEventStatusEnum): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/CalendarEvents/updatestatus/${calendarId}/${userEmail}/${newStatus}`;
    return this.http.put<ErrorModel | boolean>(url, JSON.stringify(''), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        }),

        catchError(err => this.handleError(err))
      )
  }

  //Retrun is set to always be of(true) for now for testing and to send a request to server to see response with correct models. 
  updateCalendar(calendar: CalendarEventModel): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/CalendarEvents/${calendar.id}`;

    return this.http.put<ErrorModel | boolean>(url, JSON.stringify(calendar), { observe: 'response' })
      .pipe(
        map(data => {
          if (data.status == 200) {
            return true;
          }
          if (data.status == 204) {
            return true;
          }
          return false;
        }),
        catchError(err => this.handleError(err))
      )
  }

  deleteCalendar(calendarId: string): Observable<ErrorModel | boolean | undefined> {
    const url = `${MYFILE_API_URL}/CalendarEvents/${calendarId}`;

    return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
      .pipe(
        map(data => {
          if (data && data.status == 200) {
            return true;
          }
          this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
          return false;
        }),
        catchError(err => this.returnTrue(err))
        // catchError(err => this.handleError(err))
      );
  }

  createCalendar(calendar: CalendarEventModel): Observable<CalendarEventModel | undefined> {
    const url = `${MYFILE_API_URL}/CalendarEvents/new`;

    return this.http.post<CalendarEventModel>(url, JSON.stringify(calendar)).pipe(
      map((result: CalendarEventModel) => {
        return result;
      }),
      catchError(err => this.handleError(err))
    );

  }

  exportList(exportConfig: ExportConfigModel, endpoint: string): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/${endpoint}/reports/export`;
    return this.postToExportEndpoint(url, exportConfig)
      .pipe(
        catchError(err => this.handleError(err, true))
      );
  }

  exportClientList(exportConfig: ExportConfigModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/clients/reports/export`;
    return this.postToExportEndpoint(url, exportConfig)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  exportContactsList(exportConfig: ExportConfigModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/accounts/reports/export`;
    return this.postToExportEndpoint(url, exportConfig)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  exportRequisitionsList(exportConfig: ExportConfigModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/requisitions/reports/export`;
    return this.postToExportEndpoint(url, exportConfig)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  exportTimeLinesList(exportConfig: ExportConfigModel): Observable<boolean | undefined> {
    const url = `${MYFILE_API_URL}/timelines/reports/export`;
    return this.postToExportEndpoint(url, exportConfig)
      .pipe(
        catchError(err => this.handleError(err))
      );
  }

  private postToExportEndpoint(url: string, exportConfig: ExportConfigModel): Observable<boolean | undefined> {
    return this.http.post<Blob>(url, JSON.stringify(exportConfig), { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(
        map((result: HttpResponse<Blob>) => this.saveBlobAsFromResponse(result)),
        catchError(err => this.handleError(err))
      );
  }

  private saveBlobAsFromResponse(response: HttpResponse<Blob>): boolean {
    if (response) {
      var contentDisposition = response.headers.get("Content-Disposition");

      if (contentDisposition) {

        const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = fileNameRegex.exec(contentDisposition);

        if (matches != null && matches[1]) {
          var fileName = matches[1].replace(/['"]/g, '');
          var contentType = response.headers.get("Content-Type");
          var blob = new Blob([response.body as Blob], { type: contentType as string });
          saveAs(blob, fileName);
          return true;
        }
      }
    }

    return false;
  }

  clientSignatureSubmit(token: string, signatureModel: ClientSignatureSubmitInfoModel): Observable<boolean | undefined> {
    const headers = { 'content-type': 'application/json', "Authorization": "Bearer " + token }
    return this.http.post(MYFILE_API_URL + "/signature/submit ", signatureModel, { 'headers': headers }).pipe(
      map((result) => {
        return true
      }),
      catchError(err => this.handleError(err))
    );
  }

  sendEmailSignatureRequest(signatureRequest: ClientSignatureRequestModel): Observable<boolean | undefined> {
    return this.http.post(MYFILE_API_URL + "/clients/new/signature-request ", signatureRequest)
      .pipe(
        map((result) => {
          return true
        }),
        catchError(err => this.handleError(err))
      );
  }
}
